import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Typography,
  IconButton,
  Button,
  Stack,
  Box,
  CircularProgress,
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CloseIcon from "@mui/icons-material/Close";
import DialogLoading from "../../App/DialogLoading";

const AutoSaveConfirmationDialog = ({
  open,
  label,
  onCancel,
  onLeave,
  onSave,
  onConfirm,
  section,
}) => {
  const [loading, setLoading] = useState(false);

  const handleSaveRemain = async () => {
    try {
      setLoading(true);
      await onSave?.();
      setLoading(false);
    } catch (err) {
      setLoading(false);
      console.error(err);
    }
  };

  const handleSaveContinue = async () => {
    try {
      setLoading(true);
      await onConfirm?.();
      setLoading(false);
    } catch (err) {
      setLoading(false);
      console.error(err);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onCancel}
      maxWidth='sm'
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
        },
      }}
    >
      <DialogTitle
        sx={{
          p: 3,
          pb: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Stack direction='row' spacing={1} alignItems='center'>
          <WarningAmberIcon color='warning' sx={{ fontSize: 24 }} />
          <Typography variant='h6' fontWeight={600}>
            Unsaved Changes
          </Typography>
        </Stack>
        <IconButton
          onClick={onCancel}
          size='small'
          disabled={loading}
          sx={{
            color: "text.secondary",
            "&:hover": {
              backgroundColor: "action.hover",
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ p: 3, pt: 2 }}>
        {!loading ? (
          <>
            <Typography variant='body1' color='text.secondary' sx={{ mb: 1 }}>
              You have unsaved changes in <strong>{section}</strong>.
            </Typography>
            <Typography variant='body1' color='text.secondary'>
              {label ? (
                <>
                  Would you like to continue to <strong>{label}</strong>?
                </>
              ) : (
                <>Would you like to save them before proceeding?</>
              )}
            </Typography>
          </>
        ) : (
          <DialogLoading
            title='Saving your progress...'
            content='Please wait while we sync your data.'
          />
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 3, pt: 2 }}>
        <Box
          display='flex'
          flexDirection={{ xs: "column", sm: "row" }}
          justifyContent='space-between'
          alignItems={{ xs: "flex-start", sm: "center" }}
          width='100%'
          gap={2}
        >
          <Button
            variant='text'
            onClick={onLeave}
            disabled={loading}
            sx={{
              fontSize: "0.875rem",
              textTransform: "none",
              color: "error.main",
              boxShadow: "none",
              p: 1,
              "&:hover": {
                backgroundColor: "error.lighter",
              },
            }}
          >
            Leave Without Saving
          </Button>

          <Stack direction='row' spacing={1}>
            {onSave && (
              <Button
                onClick={handleSaveRemain}
                variant='outlined'
                size='medium'
                disabled={loading}
                sx={{
                  textTransform: "none",
                  minWidth: 140,
                  borderColor: "primary.main",
                  "&:hover": {
                    borderColor: "primary.dark",
                    backgroundColor: "primary.lighter",
                  },
                }}
              >
                Save and Remain
              </Button>
            )}
            <Button
              onClick={handleSaveContinue}
              variant='contained'
              size='medium'
              disabled={loading}
              sx={{
                textTransform: "none",
                minWidth: 140,
                boxShadow: "none",
                "&:hover": {
                  boxShadow: "none",
                  backgroundColor: "primary.dark",
                },
              }}
            >
              Save and Continue
            </Button>
          </Stack>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default AutoSaveConfirmationDialog;
